import {
  CORE_MODULES,
  type CompanyLicense,
  ENTRY_END_TITLES,
  LICENSE_KIND_TITLES,
  LICENSE_STATE_TITLES,
  type License,
  type LicenseState,
  type LicenseStatus,
  MODULES,
  type PlatformEvent,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Badge, ErrorBox, Spinner, type Tone } from '../../ui/feedback';
import { KeyValue, Panel } from '../../ui/layout';
import { BillingSection } from './BillingSection';
import { InstallSection } from './InstallSection';

export const LICENSE_KEY = ['admin', 'license'];

export const STATE_TONE: Record<LicenseState, Tone> = {
  active: 'success',
  expiring: 'warning',
  grace: 'danger',
  readonly: 'danger',
  suspended: 'danger',
  none: 'neutral',
};

export function moduleTitle(code: string): string {
  return MODULES.find((m) => m.code === code)?.title ?? code;
}

/** Дни до окончания словами: «через 12 дней», «сегодня», «5 дней назад» */
export function daysLeftText(daysLeft: number | null, endsAt: string | null): string {
  if (!endsAt) return 'бессрочно';
  if (daysLeft === null) return date(endsAt);
  if (daysLeft === 0) return `${date(endsAt)} — сегодня`;
  if (daysLeft > 0) return `${date(endsAt)} — через ${daysLeft} дн.`;
  return `${date(endsAt)} — ${-daysLeft} дн. назад`;
}

export function UsageBars({ usage }: { usage: LicenseStatus['usage'] }) {
  return (
    <ul className="flex flex-col gap-3">
      {usage.map((u) => {
        // Лимит 0 — раздел не входит в тариф (камеры, техника), это не «лимит исчерпан»
        if (u.limit === 0) {
          return (
            <li key={u.kind} className="flex items-baseline justify-between gap-3 text-[14px]">
              <span className="text-ink">{u.title}</span>
              <span className="text-ink-2">не входит в тариф</span>
            </li>
          );
        }
        const pct = u.limit ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
        const full = u.limit != null && u.used >= u.limit;
        return (
          <li key={u.kind} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3 text-[14px]">
              <span className="text-ink">{u.title}</span>
              <span className={clsx('num', full ? 'font-semibold text-danger' : 'text-ink-2')}>
                {u.used.toLocaleString('ru-RU')}
                {u.limit != null ? ` из ${u.limit.toLocaleString('ru-RU')}` : ' · без ограничения'}
              </span>
            </div>
            {u.limit != null ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-tile" aria-hidden>
                <div
                  className={clsx(
                    'h-full rounded-full',
                    full ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-ink',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function LicenseEvents({ events }: { events: PlatformEvent[] }) {
  if (!events.length) return <p className="text-[14px] text-ink-2">Записей пока нет.</p>;
  return (
    <ul className="divide-y divide-line">
      {events.map((e) => (
        <li key={e.id} className="flex flex-col gap-0.5 py-2.5 text-[14px]">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-medium text-ink">{e.title}</span>
            <span className="num text-[12px] text-ink-2">{dateTime(e.createdAt)}</span>
          </div>
          <div className="text-[13px] text-ink-2">
            {[eventDetails(e), e.actorName].filter(Boolean).join(' · ')}
          </div>
        </li>
      ))}
    </ul>
  );
}

function eventDetails(e: PlatformEvent): string {
  const d = e.details as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof d.planName === 'string') parts.push(`тариф «${d.planName}»`);
  else if (typeof d.planCode === 'string') parts.push(`тариф ${d.planCode}`);
  if (typeof d.kind === 'string' && d.kind in LICENSE_KIND_TITLES)
    parts.push(LICENSE_KIND_TITLES[d.kind as keyof typeof LICENSE_KIND_TITLES].toLowerCase());
  if (typeof d.endsAt === 'string') parts.push(`до ${date(d.endsAt)}`);
  else if (d.endsAt === null && e.action === 'license.issued') parts.push('бессрочно');
  if (typeof d.reason === 'string') parts.push(`причина: ${d.reason}`);
  if (typeof d.name === 'string' && e.action.startsWith('tenant.')) parts.push(String(d.name));
  if (typeof d.company === 'string') parts.push(String(d.company));
  if (typeof d.status === 'string' && e.action === 'request.updated') parts.push(`статус: ${d.status}`);
  // Вход команды портала в компанию и передача владения (ЛЦ-10, ЛЦ-11)
  if (e.action === 'platform.entry_started' && typeof d.mode === 'string')
    parts.unshift(d.mode === 'edit' ? 'с правкой' : 'просмотр');
  if (e.action === 'platform.entry_ended' && typeof d.endReason === 'string')
    parts.push(ENTRY_END_TITLES[d.endReason as keyof typeof ENTRY_END_TITLES] ?? d.endReason);
  if (e.action === 'tenant.owner_changed' && typeof d.fullName === 'string')
    parts.push(`новый владелец: ${d.fullName}`);
  return parts.join(', ');
}

export function LicenseHistory({ history }: { history: License[] }) {
  if (!history.length) return <p className="text-[14px] text-ink-2">Прежних лицензий нет.</p>;
  return (
    <ul className="divide-y divide-line">
      {history.map((l) => (
        <li key={l.id} className="flex flex-col gap-0.5 py-2.5 text-[14px]">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-medium text-ink">
              {l.planName} · {LICENSE_KIND_TITLES[l.kind]}
            </span>
            <span className="num text-[12px] text-ink-2">
              {date(l.startsAt)} — {l.endsAt ? date(l.endsAt) : 'бессрочно'}
            </span>
          </div>
          {l.note ? <div className="text-[13px] text-ink-2">{l.note}</div> : null}
        </li>
      ))}
    </ul>
  );
}

/** Администрирование → Лицензия (ЛЦ-03, ЛЦ-04): тариф, срок, использование лимитов, история, куда писать */
export function LicensePage() {
  const q = useQuery({ queryKey: LICENSE_KEY, queryFn: () => api<CompanyLicense>('/admin/license') });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const { status, license, history, events, contact } = q.data;
  const modules = status.modules;
  const included = modules ? MODULES.filter((m) => modules.includes(m.code)) : [];
  const excluded = modules
    ? MODULES.filter((m) => !modules.includes(m.code) && !CORE_MODULES.includes(m.code))
    : [];
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="flex flex-col gap-5">
        <Panel
          title="Лицензия компании"
          meta="раздел 6.29 ТЗ"
          actions={<Badge tone={STATE_TONE[status.state]}>{LICENSE_STATE_TITLES[status.state]}</Badge>}
        >
          <KeyValue
            items={[
              { label: 'Тариф', value: status.planName ?? '—' },
              { label: 'Вид', value: status.kind ? LICENSE_KIND_TITLES[status.kind] : '—' },
              { label: 'Начало', value: status.startsAt ? date(status.startsAt) : '—' },
              { label: 'Окончание', value: daysLeftText(status.daysLeft, status.endsAt) },
              { label: 'Мест', value: status.seats != null ? status.seats : 'по тарифу' },
              ...(license?.note ? [{ label: 'Заметка', value: license.note }] : []),
            ]}
          />
          <p className="mt-4 text-[13px] text-ink-2">
            За 30, 14 и 3 дня до окончания администратор получает предупреждение здесь и в «Ждёт меня». После
            окончания — 14 дней льготного периода, затем портал переходит в режим «только чтение»: данные
            видны, выгрузки работают, изменения закрыты. Данные компании не удаляются без её письменного
            запроса.
          </p>
          <div className="mt-4 rounded-md bg-surface-2 px-4 py-3 text-[14px]">
            <div className="font-medium text-ink">Расширить тариф или продлить лицензию</div>
            <div className="mt-1 text-ink-2">
              {contact ? (
                <>
                  Напишите владельцу портала:{' '}
                  {contact.includes('@') ? (
                    <a
                      className="font-medium text-ink underline underline-offset-2"
                      href={`mailto:${contact}`}
                    >
                      {contact}
                    </a>
                  ) : (
                    <span className="font-medium text-ink">{contact}</span>
                  )}
                  .
                </>
              ) : (
                'Обратитесь к владельцу портала — он выпустит новую лицензию, и она заработает сразу.'
              )}
            </div>
          </div>
        </Panel>

        <Panel title="Использование лимитов" meta="ЛЦ-04">
          <UsageBars usage={status.usage} />
        </Panel>
        <InstallSection />
        <BillingSection />
      </div>

      <div className="flex flex-col gap-5">
        <Panel title="Разделы по тарифу">
          {modules === null || !excluded.length ? (
            <p className="text-[14px] text-ink-2">Все разделы портала.</p>
          ) : (
            <div className="flex flex-col gap-3 text-[14px]">
              <div>
                <div className="mb-1.5 text-[13px] text-ink-2">Входят</div>
                <div className="flex flex-wrap gap-1.5">
                  {included.map((m) => (
                    <Badge key={m.code} tone="success">
                      {m.title}
                    </Badge>
                  ))}
                  {!included.length ? <span className="text-ink-2">только основа портала</span> : null}
                </div>
              </div>
              {excluded.length ? (
                <div>
                  <div className="mb-1.5 text-[13px] text-ink-2">Не входят (скрыты из меню)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {excluded.map((m) => (
                      <Badge key={m.code}>{m.title}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Panel>
        <Panel title="История продлений и изменений" meta="ЛЦ-02">
          <LicenseEvents events={events} />
        </Panel>
        {history.length ? (
          <Panel title="Прежние лицензии">
            <LicenseHistory history={history} />
          </Panel>
        ) : null}
      </div>
    </div>
  );
}
